import clsx from 'clsx';

import { getArrayItemByIndex } from 'shared/lib/utils';
import { Spinner } from 'shared/ui/spinner';
import { NoItemsError } from 'shared/ui/errors';
import { Carousel } from 'shared/ui/carousel';
// eslint-disable-next-line boundaries/element-types
import { useUserProgramsData } from 'widgets/programs/model';
import { useIsWalkthroughActive } from 'features/walkthrough';
import { Recommendation } from 'shared/api/insights';

import { RecommendationsPreviewItem } from '../recommendations-preview-item';
import { WalkthroughRecommendationsItem } from '../../walkthrough-recommendations-item';
import {
  RECOMMENDATION_THUMBNAIL_COLORS,
  RECOMMENDATIONS_PREVIEW_CAROUSEL_ID,
} from '../../../constants';

type RecommendationsPreviewContentProps = {
  recommendations: Recommendation[];
  isLoading?: boolean;
  withCarousel?: boolean;
};

export const RecommendationsPreviewContent = ({
  recommendations,
  isLoading,
  withCarousel,
}: RecommendationsPreviewContentProps) => {
  const { userProgramsData, isLoading: isUserProgramsLoading } =
    useUserProgramsData();

  const isWalkthroughActive = useIsWalkthroughActive();

  const recommendationsItems = recommendations.map(
    (
      {
        id,
        start_relevant_date,
        app_product_names,
        type,
        content,
        dynamicContent,
        created_date,
      },
      index,
    ) => (
      <RecommendationsPreviewItem
        id={id}
        key={id}
        insightType={type}
        content={content}
        dynamicContent={dynamicContent}
        thumbnailFallbackColor={getArrayItemByIndex(
          index,
          RECOMMENDATION_THUMBNAIL_COLORS,
        )}
        programAssetId={app_product_names[0]}
        userPrograms={!isUserProgramsLoading ? userProgramsData : undefined}
        startRelevantDate={start_relevant_date}
        classNames={clsx({
          'w-100': withCarousel,
        })}
        createdDate={created_date}
      />
    ),
  );

  if (isLoading) {
    return (
      <Spinner
        isCentered={false}
        isDark
        isLarge
        classNames="mx-auto my-5"
        testId="recommendation-spinner"
      />
    );
  }

  if (!recommendations.length && isWalkthroughActive) {
    return <WalkthroughRecommendationsItem />;
  }

  if (!recommendations.length) {
    return <NoItemsError />;
  }

  if (withCarousel) {
    return (
      <Carousel
        arrowsContainerId={RECOMMENDATIONS_PREVIEW_CAROUSEL_ID}
        prevButtonAriaLabel="Previous Recommendation"
        nextButtonAriaLabel="Next Recommendation"
        testId={RECOMMENDATIONS_PREVIEW_CAROUSEL_ID}
        isCycled
      >
        {recommendationsItems}
      </Carousel>
    );
  }

  return <>{recommendationsItems}</>;
};
